import { getInitialState, getWidget, getWidgetsMarkup, isServer, removeWidgetsMarkup, } from '@okapi/dom-utils';
import React, { createElement, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { hydrate, unmountComponentAtNode } from 'react-dom';
import { useId } from '../hooks';
import { Context } from './context';
import { useRemoteContentProps } from './hooks/use-remote-content-props';
import { useRemoteWrapperProps } from './hooks/use-remote-wrapper-props';
import { GroupContext } from './remote-group';
const displayNone = { display: 'none' };
/**
 * Компонент <Remote/>
 * TODO (Client) сделать тест для проверки рендера и ре-рендера remote компонентов
 */
export const Remote = memo(function Remote({ id, widget, className, fallback, data: incomingData, batching, loading, onEvent, ...props }) {
    var _a;
    const context = useContext(Context);
    const widgetId = useId(widget, incomingData, id);
    const groupId = useContext(GroupContext).id;
    const contentHtml = useRef({
        __html: getWidgetsMarkup(widgetId),
    });
    const contentRef = useRef(null);
    const createWidget = getWidget(widget);
    /** Определяет добавлен ли виджет в рантайме или при SSR */
    const addedOnClient = !isServer() && (!getInitialState(widgetId) || !getWidgetsMarkup(widgetId));
    /** Виджет добавлен в SSR с fallback в разметке */
    const fromServerWithFallback = useMemo(() => !addedOnClient && !createWidget, []);
    /** Нужно ли гидрировать fallback */
    const needHydrationFallback = useMemo(() => !addedOnClient && fromServerWithFallback, []);
    /** Нужно ли гидрировать основной контент */
    const [needHydration, setNeedHydration] = useState(!needHydrationFallback && !addedOnClient && !fromServerWithFallback);
    /** виджет загружен */
    const [loaded, setLoaded] = useState(!!createWidget);
    /** fallback был гидрирован */
    const [fallbackHydrated, setFallbackHydrated] = useState(false);
    /** контент после ssr, выводится напрямую в html компонента, нужно, чтобы react не вычищал разметку до гидрации */
    const [withDangerousHTML, setWithDangerousHTML] = useState(!isServer() && !addedOnClient);
    /** уже есть контент при старте */
    const withInitialContent = useMemo(() => Boolean(addedOnClient && createWidget), []);
    const [isFallback, setIsFallback] = useState(false);
    /****/
    const [data, setData] = useState(incomingData);
    const [isBundleLoading, setIsBundleLoading] = useState(!createWidget);
    const [isStateLoading, setIsStateLoading] = useState(addedOnClient && !data);
    const [stateLoaded, setStateLoaded] = useState(!addedOnClient || !!data);
    const addedOnClientAndLoadingState = useMemo(() => !incomingData && isStateLoading && !stateLoaded, []);
    const sendEvent = (e) => {
        const handler = props[`on${e}`];
        handler && handler(widget, widgetId);
        onEvent && onEvent(e, widget, widgetId);
    };
    // загрузка стейта на клиенте (если его нет)
    useEffect(() => {
        if (!data && !stateLoaded) {
            setIsStateLoading(true);
            if (context.remote) {
                sendEvent('StateLoading');
                context.remote
                    .loadState(widget)
                    .then(state => {
                    sendEvent('StateLoaded');
                    setStateLoaded(true);
                    setIsStateLoading(false);
                    setData(state);
                })
                    .catch(() => {
                    sendEvent('Fallback');
                    setStateLoaded(true);
                    setIsStateLoading(false);
                    setData(undefined);
                    setIsFallback(true);
                    setContent(fallback);
                });
            }
        }
    }, []);
    /****/
    /** начальный контент */
    const initialContent = useMemo(() => {
        if (isServer()) {
            setIsFallback(true);
            return fallback;
        }
        if (withDangerousHTML) {
            setIsFallback(false);
            return null;
        }
        if (addedOnClient) {
            if (!stateLoaded || isStateLoading || isBundleLoading) {
                setIsFallback(false);
            }
            else {
                if (createWidget) {
                    const state = getInitialState(widgetId);
                    const app = createWidget(state);
                    setIsFallback(false);
                    return createElement(app, data);
                }
                else {
                    if (!isBundleLoading) {
                        // todo
                        sendEvent('BundleLoading');
                        setIsBundleLoading(true);
                    }
                    return null;
                }
            }
        }
        if (!(addedOnClient && (!stateLoaded || isStateLoading))) {
            sendEvent('Fallback');
            setIsFallback(true);
        }
        if (fromServerWithFallback) {
            return null;
        }
        if (isStateLoading) {
            return loading;
        }
        return fromServerWithFallback ? null : fallback;
    }, []);
    /** выводимый контент */
    let [content, setContent] = useState(initialContent);
    // запускается после загрузки виджета, либо сразу, если уже загружен
    useEffect(() => {
        if (loaded && createWidget) {
            if (!withInitialContent && !isStateLoading) {
                sendEvent('BundleLoaded');
                const state = addedOnClientAndLoadingState && stateLoaded ? data : getInitialState(widgetId);
                const app = createWidget(state);
                const element = createElement(app, data);
                if (fallbackHydrated) {
                    // если до этого был гидрирован fallback, то нужно его "уничтожить" и продолжить в следующем тике
                    if (contentRef.current) {
                        unmountComponentAtNode(contentRef.current);
                    }
                    setFallbackHydrated(false);
                }
                else {
                    if (needHydration) {
                        // гидрируем после SSR
                        hydrate(element, contentRef.current);
                        setNeedHydration(false);
                    }
                    else {
                        // устанавливаем контент, на клиенте при добавлении на лету,
                        // либо после SSR с fallback и последующей загрузкой виджите
                        setWithDangerousHTML(false);
                        setIsFallback(false);
                        setContent(element);
                    }
                }
            }
        }
        return () => {
            // подчищаем после удаления компонента
            if (needHydration
                && contentRef.current !== null
                // OKAPI-481 React выбросит ошибку, если попытаемся удалить node'у, которая была отрендерена
                // [packages/react-dom/src/__tests__/ReactMountDestruction-test.js#L52](t.ly/RgqS)
                && !contentRef.current.parentNode) {
                unmountComponentAtNode(contentRef.current);
            }
            removeWidgetsMarkup(widgetId);
        };
    }, [loaded, fallbackHydrated, data]);
    // запускается при первичном рендере
    useEffect(() => {
        // гидрируем fallback если нужно
        if (needHydrationFallback) {
            const fallbackApp = () => React.createElement(React.Fragment, null, fallback);
            const element = createElement(fallbackApp);
            hydrate(element, contentRef.current);
            setFallbackHydrated(true);
        }
        if (!createWidget) {
            // загружаем виджет на клиенте, если он изначально еще не был загружен (например, когда ssr c fallback)
            if (context && context.remote) {
                context.remote
                    .add({
                    id: widgetId,
                    widget,
                    data,
                })
                    .then(() => {
                    setLoaded(true);
                });
            }
        }
        return () => {
            // подчищаем после удаления компонента
            if (needHydration && contentRef.current !== null) {
                unmountComponentAtNode(contentRef.current);
            }
            removeWidgetsMarkup(widgetId);
        };
    }, []);
    if (isServer()) {
        // добавляем виджет в очередь на загрузку на сервере
        (_a = context.remote) === null || _a === void 0 ? void 0 : _a.add({
            id: widgetId,
            groupId,
            widget,
            data,
            batching,
        });
    }
    const wrapperProps = useRemoteWrapperProps({
        widget,
        widgetId,
        groupId,
        batching: !!batching,
        className,
    });
    const contentProps = useRemoteContentProps({
        widgetId,
        resetStyles: !isFallback && !isStateLoading && !isBundleLoading,
        ref: contentRef,
        dangerousHTML: withDangerousHTML && contentHtml.current,
        fromServerWithFallback,
        content,
    });
    return (React.createElement("div", { ...wrapperProps },
        React.createElement("div", { ...contentProps }, content),
        React.createElement("div", { "data-end": widgetId, style: displayNone })));
});
